import { useState, type ReactNode } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useTheme } from '@/contexts/ThemeContext'
import { cn } from '@/lib/utils'
import { GraduationCap, Menu, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface NavItem {
  name: string
  href: string
  icon: LucideIcon
  locked?: boolean
  waiting?: boolean
}

interface SidebarLayoutProps {
  navigation: NavItem[]
  dashboardHref: string
  sidebarBadge?: ReactNode
  sidebarFooter?: ReactNode
  headerActions?: ReactNode
  floatingElement?: ReactNode
  lockIcon?: ReactNode
}

export default function SidebarLayout({
  navigation,
  dashboardHref,
  sidebarBadge,
  sidebarFooter,
  headerActions,
  floatingElement,
}: SidebarLayoutProps) {
  const { ageTheme } = useTheme()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const isActive = (href: string) => {
    if (href === dashboardHref) return location.pathname === dashboardHref
    return location.pathname.startsWith(href)
  }

  return (
    <div className={cn('min-h-screen bg-background', `theme-${ageTheme}`)}>
      {/* Sidebar móvil */}
      <div className={cn(
        'fixed inset-0 z-50 lg:hidden',
        sidebarOpen ? 'block' : 'hidden'
      )}>
        <div
          className="fixed inset-0 bg-black/50"
          role="button"
          tabIndex={0}
          aria-label="Cerrar menú"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setSidebarOpen(false) }}
        />
        <div className="fixed inset-y-0 left-0 w-64 bg-card shadow-xl">
          <div className="flex items-center justify-between p-4 border-b">
            <Link to={dashboardHref} className="flex items-center gap-2">
              <span className="text-xl font-bold text-primary">
                Nomad<span className="text-[#EF4135]">Lexis</span>
              </span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg hover:bg-muted"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="p-4 space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                  isActive(item.href)
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-foreground'
                )}
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.name}</span>
                {item.locked}
                {item.waiting}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Sidebar desktop */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:flex lg:w-64 lg:flex-col bg-card border-r">
        <div className="flex items-center gap-2 p-4 border-b">
          <Link to={dashboardHref} className="flex items-center gap-2">
            <GraduationCap className="w-8 h-8 text-primary" />
            <span className="text-xl font-bold">
              Nomad<span className="text-[#EF4135]">Lexis</span>
            </span>
          </Link>
        </div>

        {sidebarBadge}

        <nav className="flex-1 p-4 space-y-2">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                isActive(item.href)
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-foreground'
              )}
            >
              <item.icon className="w-5 h-5" />
              <span className="flex-1">{item.name}</span>
            {item.locked}
            </Link>
          ))}
        </nav>

        {sidebarFooter}
      </aside>

      {/* Contenido principal */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-card/80 backdrop-blur-md border-b">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-muted lg:hidden"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="hidden lg:block" />
            <div className="flex items-center gap-3">
              {headerActions}
            </div>
          </div>
        </header>

        <main className="p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {floatingElement}
    </div>
  )
}
